/**
 * FloatingPanel — the shared floating window shell (plan 15.4, stage F1).
 *
 * States: hidden, bubble, small, maximised (see panelStateMachine.js).
 * Extracted from tse/DiagnosticMapWindow.jsx: drag by the header, resize from
 * right/bottom/corner, z ladder 60/70/80. Every new behaviour is opt-in, so a
 * caller that passes none of the new props gets the legacy map window shell:
 *   placement="viewport"      fixed to the screen, first open at the top right
 *   viewportMargin            keep >= N px on screen (null = no clamp)
 *   avoidSelector, avoidGap   default spot (bubble + first open) sits below these header bars
 *   persistKey                save position/size/state in localStorage
 *   keyboardControls          arrows move, Shift+arrows resize, Esc steps down
 *   trapFocusWhenMaximised    modal focus trap + restore while maximised
 *   raiseOnFocus              shared raise-on-focus counter within the rung
 *   narrowBreakpoint          at or under N px: bubble only, opening maximises
 *   onResize                  ResizeObserver callback ({width, height})
 *   headerActions             extra controls in the default header, left of the window icons
 * The brain popover spreads BRAIN_PANEL_PRESET to turn them all on.
 */
import React, {
  forwardRef, useCallback, useEffect, useId, useImperativeHandle, useRef, useState,
} from "react";

import { FloatingPanelBubble, FloatingPanelHeader } from "./FloatingPanelChrome";
import {
  clampRect, clampSize, defaultTopRightPosition, isArrowKey, keyboardStep, readViewport, safeTopFor,
} from "./floatingGeometry";
import {
  BRAIN_DEFAULT_SIZE, LEGACY_ANCHOR, LEGACY_DEFAULT_SIZE, LEGACY_MIN_SIZE,
  PANEL_ACTIONS, PANEL_STATES, PLACEMENT, RESIZE_DIRECTIONS,
} from "./floatingPanelConstants";
import { computeFrameStyle, isNumericSize } from "./frameStyle";
import {
  useFocusTrap, useHeaderBottom, useMediaQuery, useResizeCallback, useViewport,
} from "./panelDomHooks";
import { useRaiseOrder, zIndexFor } from "./panelLayers";
import { loadPanelPrefs, persistableState, savePanelPrefs } from "./panelPersistence";
import { effectivePanelState, isVisibleState, nextPanelState } from "./panelStateMachine";
import { usePanelPointer } from "./usePanelPointer";
import "./brain.css";

const { HIDDEN, BUBBLE, SMALL, MAXIMISED } = PANEL_STATES;
const RESIZE_ORDER = [RESIZE_DIRECTIONS.RIGHT, RESIZE_DIRECTIONS.BOTTOM, RESIZE_DIRECTIONS.CORNER];
const DEFAULT_RESIZE_CLASSES = Object.freeze({
  [RESIZE_DIRECTIONS.RIGHT]: "d5-brain-resize d5-brain-resize--right",
  [RESIZE_DIRECTIONS.BOTTOM]: "d5-brain-resize d5-brain-resize--bottom",
  [RESIZE_DIRECTIONS.CORNER]: "d5-brain-resize d5-brain-resize--corner",
});

const geometryFromPrefs = (prefs) => ({
  pos: Number.isFinite(prefs.x) ? { x: prefs.x, y: prefs.y } : null,
  size: Number.isFinite(prefs.width) ? { width: prefs.width, height: prefs.height } : null,
});

const FloatingPanel = forwardRef(function FloatingPanel({
  id,
  title = "",
  panelState,
  defaultPanelState = HIDDEN,
  onPanelStateChange,
  placement = PLACEMENT.ANCHORED,
  anchor = LEGACY_ANCHOR,
  defaultSize = LEGACY_DEFAULT_SIZE,
  minSize = LEGACY_MIN_SIZE,
  viewportMargin = null,
  avoidSelector = null,
  avoidGap = 0,
  draggable = true,
  resizable = true,
  keyboardControls = false,
  trapFocusWhenMaximised = false,
  raiseOnFocus = false,
  narrowBreakpoint = null,
  persistKey = null,
  onResize,
  className = "",
  style,
  frameProps,
  renderHeader,
  headerActions = null,
  renderBubble,
  bubbleName = "panel",
  bubbleCount = 0,
  bubbleClassName,
  pulse = false,
  resizeHandleClassNames = DEFAULT_RESIZE_CLASSES,
  children,
}, ref) {
  const reactId = useId();
  const panelId = id || `floating-panel-${reactId.replace(/:/g, "")}`;
  const titleId = `${panelId}-title`;

  const [prefs] = useState(() => loadPanelPrefs(persistKey));
  const [internalState, setInternalState] = useState(defaultPanelState);
  const isControlled = panelState !== undefined;
  const current = isControlled ? panelState : internalState;
  const narrow = useMediaQuery(narrowBreakpoint ? `(max-width: ${narrowBreakpoint}px)` : null);
  const shown = effectivePanelState(current, narrow);

  const [geometry, setGeometry] = useState(() => geometryFromPrefs(prefs));
  const [interacting, setInteracting] = useState(false);
  const frameRef = useRef(null);
  const bubbleRef = useRef(null);
  const isViewport = placement === PLACEMENT.VIEWPORT;
  const viewport = useViewport(isViewport || viewportMargin != null);
  // Only the DEFAULT spot (bubble, and the panel before the user moves it)
  // sits below the app header; drag, keys, saved positions and maximised
  // clamp to the viewport alone (owner: the panel may go over the header).
  const headerBottom = useHeaderBottom(avoidSelector, isViewport && Boolean(avoidSelector), shown);
  const minTop = safeTopFor(headerBottom, avoidGap);

  /* ── state machine ── */
  const currentRef = useRef(current);
  currentRef.current = current;
  const lastVisibleRef = useRef(prefs.state || null);
  const onChangeRef = useRef(onPanelStateChange);
  onChangeRef.current = onPanelStateChange;
  useEffect(() => { if (isVisibleState(current)) lastVisibleRef.current = current; }, [current]);

  const dispatch = useCallback((action) => {
    const prev = currentRef.current;
    const next = nextPanelState(prev, action, { narrow, restoreState: lastVisibleRef.current });
    if (next === prev) return prev;
    if (!isControlled) setInternalState(next);
    onChangeRef.current?.(next, action, prev);
    return next;
  }, [isControlled, narrow]);

  useImperativeHandle(ref, () => ({
    dispatch,
    show: () => dispatch(PANEL_ACTIONS.SHOW),
    hide: () => dispatch(PANEL_ACTIONS.HIDE),
    getState: () => currentRef.current,
  }), [dispatch]);

  /* ── geometry ── */
  const clampTo = useCallback((pos, size) => (
    viewportMargin == null ? { pos, size } : clampRect(pos, size, minSize, readViewport(), viewportMargin)
  ), [minSize, viewportMargin]);

  const { startDrag, startResize } = usePanelPointer({
    frameRef,
    minSize,
    onDrag: (pos, size) => setGeometry((g) => ({ ...g, pos: clampTo(pos, size).pos })),
    onResize: (size) => setGeometry((g) => ({
      ...g, size: viewportMargin == null ? size : clampSize(size, minSize, readViewport()),
    })),
    onInteractionChange: setInteracting,
  });

  let renderSize = geometry.size || (isViewport && !isNumericSize(defaultSize) ? BRAIN_DEFAULT_SIZE : defaultSize);
  let renderPos = geometry.pos;
  if (isViewport && !renderPos) renderPos = defaultTopRightPosition(renderSize, viewport, viewportMargin ?? 0, minTop);
  if (viewportMargin != null && renderPos && isNumericSize(renderSize)) {
    ({ pos: renderPos, size: renderSize } = clampRect(renderPos, renderSize, minSize, viewport, viewportMargin));
  }

  // Without persistence a closed panel forgets where it was dragged (legacy:
  // the map window resets its drag position on close but keeps its size).
  useEffect(() => {
    if (current === HIDDEN && !persistKey) setGeometry((g) => (g.pos ? { ...g, pos: null } : g));
  }, [current, persistKey]);

  useEffect(() => {
    if (!persistKey || interacting) return;
    const patch = {};
    const savedState = persistableState(current);
    if (savedState) patch.state = savedState;
    if (geometry.pos) Object.assign(patch, geometry.pos);
    if (isNumericSize(geometry.size)) Object.assign(patch, geometry.size);
    savePanelPrefs(persistKey, patch);
  }, [persistKey, current, geometry, interacting]);

  /* ── keyboard ── */
  const onHandleKeyDown = useCallback((e) => {
    if (!isArrowKey(e.key) || e.altKey || e.ctrlKey || e.metaKey) return;
    const el = frameRef.current;
    if (!el) return;
    e.preventDefault();
    const rect = el.getBoundingClientRect();
    const stepped = keyboardStep(
      { pos: { x: rect.left, y: rect.top }, size: { width: rect.width, height: rect.height } },
      e.key, e.shiftKey, minSize,
    );
    const next = clampTo(stepped.pos, stepped.size);
    setGeometry((g) => (e.shiftKey ? { ...g, size: next.size } : { ...g, pos: next.pos }));
  }, [clampTo, minSize]);

  const onFrameKeyDown = useCallback((e) => {
    if (!keyboardControls || e.key !== "Escape" || e.defaultPrevented) return;
    e.preventDefault();
    e.stopPropagation();
    dispatch(PANEL_ACTIONS.ESCAPE);
  }, [keyboardControls, dispatch]);

  /* ── focus, layers, resize observer ── */
  const trapActive = trapFocusWhenMaximised && shown === MAXIMISED;
  useFocusTrap(frameRef, trapActive);

  const prevShownRef = useRef(shown);
  useEffect(() => {
    const prev = prevShownRef.current;
    prevShownRef.current = shown;
    if (!keyboardControls || shown !== BUBBLE || (prev !== SMALL && prev !== MAXIMISED)) return;
    const active = document.activeElement;
    if (!active || active === document.body) bubbleRef.current?.focus();
  }, [shown, keyboardControls]);

  const [raiseOffset, raise] = useRaiseOrder(panelId, raiseOnFocus && shown !== HIDDEN);
  const zIndex = zIndexFor(shown, raiseOffset);
  useResizeCallback(frameRef, onResize, Boolean(onResize) && (shown === SMALL || shown === MAXIMISED));

  if (shown === HIDDEN) return null;

  if (shown === BUBBLE) {
    const open = () => dispatch(PANEL_ACTIONS.OPEN_BUBBLE);
    // Same top as the panel's default spot; undefined keeps the CSS top.
    const bubbleTop = isViewport && minTop > 0 ? Math.max(viewportMargin ?? 0, minTop) : undefined;
    if (renderBubble) return renderBubble({ open, count: bubbleCount, zIndex, top: bubbleTop });
    return (
      <FloatingPanelBubble
        ref={bubbleRef} name={bubbleName} count={bubbleCount} pulse={pulse}
        className={bubbleClassName} zIndex={zIndex} top={bubbleTop} onOpen={open}
      />
    );
  }

  const keyboardHandleProps = keyboardControls ? {
    tabIndex: 0,
    role: "group",
    "aria-label": `${title}: arrow keys move, Shift plus arrow keys resize`,
    onKeyDown: onHandleKeyDown,
  } : {};
  const dragHandleProps = draggable && shown === SMALL
    ? { onPointerDown: startDrag, "data-floating-drag-handle": "", ...keyboardHandleProps }
    : undefined;
  const api = {
    panelState: shown,
    title,
    titleId,
    isNarrow: narrow,
    dragHandleProps,
    dispatch,
    minimise: () => dispatch(PANEL_ACTIONS.MINIMISE),
    maximise: () => dispatch(PANEL_ACTIONS.MAXIMISE),
    close: () => dispatch(PANEL_ACTIONS.CLOSE),
  };

  return (
    <div
      ref={frameRef}
      id={panelId}
      role="dialog"
      aria-modal={trapActive ? "true" : "false"}
      aria-labelledby={renderHeader ? undefined : titleId}
      aria-label={renderHeader ? title || undefined : undefined}
      tabIndex={keyboardControls || trapFocusWhenMaximised ? -1 : undefined}
      data-panel-state={shown}
      className={typeof className === "function" ? className(shown) : className}
      style={{ ...computeFrameStyle({ shown, pos: renderPos, size: renderSize, anchor, zIndex }), ...style }}
      onKeyDown={onFrameKeyDown}
      onPointerDownCapture={raiseOnFocus ? raise : undefined}
      onFocusCapture={raiseOnFocus ? raise : undefined}
      {...frameProps}
    >
      {renderHeader ? renderHeader(api) : <FloatingPanelHeader {...api} headerActions={headerActions} />}
      {typeof children === "function" ? children(api) : children}
      {shown === SMALL && resizable && RESIZE_ORDER.map((dir) => (
        <div
          key={dir}
          className={resizeHandleClassNames[dir]}
          data-floating-resize={dir}
          aria-hidden="true"
          onPointerDown={(e) => startResize(e, dir)}
        />
      ))}
    </div>
  );
});

export default FloatingPanel;
